import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, Inbox } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  getCatalogoExamesControllerPendenciasQueryKey,
  useCatalogoExamesControllerPendencias,
  useCatalogoExamesControllerResolverPendencia,
} from '@/api/generated/admin-catalogo-exames/admin-catalogo-exames';
import type { PendenciaDoLaboratorioResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { rotaDoExameDoCatalogo, ROTAS } from '@/shared/lib/rotas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const NOVO_ANALITO = '__novo__';

/**
 * Pendências do laboratório (B9-5).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  Nome de teste que o Vertis manda e que não casa com analito nenhum: o exame
 *  entra, aquele resultado não, e o laudo sai sem a linha. Antes desta tela o
 *  caso vivia só como um `warn` no log do job — onde ninguém olha.
 *
 *  Duas saídas, e a escolha é clínica:
 *   · é o MESMO parâmetro com outro nome → vira apelido do analito existente;
 *   · é um parâmetro A MAIS → cria o analito, já com o nome do laboratório.
 *  E "ignorar", para o que não é analito ("MATERIAL: SORO" chega por aqui).
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function PendenciasLaboratorioPage() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();
  const [escolhas, setEscolhas] = useState<Record<string, string>>({});

  const { data, isPending, isError, refetch } = useCatalogoExamesControllerPendencias();
  const resolver = useCatalogoExamesControllerResolverPendencia();

  const podeEditar = pode('admin_catalogo_exames.editar');

  async function aplicar(pendencia: PendenciaDoLaboratorioResponse, acao: 'RESOLVER' | 'IGNORAR') {
    const escolha = escolhas[pendencia.id];

    const corpo =
      acao === 'IGNORAR'
        ? ({ acao: 'IGNORAR' } as const)
        : escolha === NOVO_ANALITO
          ? ({ acao: 'NOVO_ANALITO' } as const)
          : ({ acao: 'APELIDO', analitoId: escolha } as const);

    try {
      await resolver.mutateAsync({ pendenciaId: pendencia.id, data: corpo });
      await queryClient.invalidateQueries({
        queryKey: getCatalogoExamesControllerPendenciasQueryKey(),
      });
      toast.success(
        acao === 'IGNORAR'
          ? 'Pendência arquivada.'
          : `"${pendencia.nomeDoTeste}" passa a casar no próximo ciclo do sync.`,
      );
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível resolver.');
    }
  }

  if (isPending) return <EstadoCarregando rotulo="Carregando pendências..." />;
  if (isError || !data) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <Link
        to={ROTAS.adminCatalogoExames}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-brand transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar ao portfólio
      </Link>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Pendências do laboratório
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Nomes de teste que chegaram do Vertis e não casaram com nenhum analito. Enquanto
            estiverem aqui, esses resultados não entram no laudo.
          </p>
        </div>

        {data.itens.length === 0 ? (
          <EstadoVazio
            icone={CheckCircle2}
            titulo="Nada pendente"
            descricao="Todo resultado que chegou do laboratório casou com um analito do portfólio."
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome no laboratório</TableHead>
                  <TableHead>Exame</TableHead>
                  <TableHead className="w-24">Ocorrências</TableHead>
                  <TableHead className="w-64">Pertence a</TableHead>
                  <TableHead className="w-44" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.itens.map((pendencia) => {
                  const semExame = pendencia.catalogoExameId === null;
                  const escolha = escolhas[pendencia.id];

                  return (
                    <TableRow key={pendencia.id}>
                      <TableCell className="font-medium text-foreground">
                        {pendencia.nomeDoTeste}
                      </TableCell>

                      <TableCell className="text-muted-foreground">
                        {semExame ? (
                          <Badge variant="outline" className="text-destructive border-destructive/40">
                            Código {pendencia.codigoVertis} não está no portfólio
                          </Badge>
                        ) : (
                          <Link
                            to={rotaDoExameDoCatalogo(pendencia.catalogoExameId!)}
                            className="hover:text-brand transition-colors"
                          >
                            {pendencia.exameNome}
                            <span className="block text-xs">Vertis: {pendencia.codigoVertis}</span>
                          </Link>
                        )}
                      </TableCell>

                      <TableCell className="text-muted-foreground">
                        {pendencia.ocorrencias}
                      </TableCell>

                      <TableCell>
                        {podeEditar && !semExame && (
                          <Select
                            value={escolha ?? ''}
                            onValueChange={(v) =>
                              setEscolhas((atual) => ({ ...atual, [pendencia.id]: v }))
                            }
                          >
                            <SelectTrigger aria-label={`Destino de ${pendencia.nomeDoTeste}`}>
                              <SelectValue placeholder="Escolha o analito..." />
                            </SelectTrigger>
                            <SelectContent>
                              {pendencia.analitos.map((analito) => (
                                <SelectItem key={analito.id} value={analito.id}>
                                  {analito.nome}
                                </SelectItem>
                              ))}
                              <SelectItem value={NOVO_ANALITO}>
                                + Criar analito &quot;{pendencia.nomeDoTeste}&quot;
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>

                      <TableCell className="text-right whitespace-nowrap">
                        {podeEditar && (
                          <>
                            <Button
                              size="sm"
                              disabled={semExame || !escolha || resolver.isPending}
                              onClick={() => void aplicar(pendencia, 'RESOLVER')}
                            >
                              Aplicar
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={resolver.isPending}
                              onClick={() => void aplicar(pendencia, 'IGNORAR')}
                            >
                              Ignorar
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {data.itens.length > 0 && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Inbox className="w-3.5 h-3.5" />
          Resolver aqui não recupera o resultado que já chegou: ele entra no próximo ciclo do sync,
          quando o exame for reenviado.
        </p>
      )}
    </div>
  );
}
